
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { Clock, MapPin, User, Phone, Package } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface CompletedOrder {
  id: string;
  customer_id: string;
  delivery_address: string;
  delivery_type: string;
  total_amount: number;
  delivery_fee: number;
  status: string;
  created_at: string;
  completed_at: string;
  customer_name: string | null;
  customer_phone: string | null;
  notes: string | null;
  order_items: {
    quantity: number;
    price: number;
    menu_items: {
      name: string;
    };
  }[];
}

interface CompletedOrdersViewProps {
  restaurantId: string;
}

const CompletedOrdersView = ({ restaurantId }: CompletedOrdersViewProps) => {
  const [orders, setOrders] = useState<CompletedOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompletedOrders();
  }, [restaurantId]);

  const fetchCompletedOrders = async () => {
    try {
      // Get orders completed in the last 24 hours to handle timezone issues
      const last24Hours = new Date();
      last24Hours.setHours(last24Hours.getHours() - 24);
      
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            quantity,
            price,
            menu_items (
              name
            )
          )
        `)
        .eq('restaurant_id', restaurantId)
        .gte('completed_at', last24Hours.toISOString())
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false });

      if (error) {
        console.error('Error fetching completed orders:', error);
      } else {
        console.log('Fetched completed orders:', data);
        setOrders(data || []);
      }
    } catch (error) {
      console.error('Error fetching completed orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'picked_up':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Completed Orders Today</h3>
          <p className="text-gray-600">Completed orders will appear here.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 mb-4">
        {orders.length} order{orders.length !== 1 ? 's' : ''} completed today
      </div>

      <div className="space-y-4">
        {orders.map((order) => (
          <Card key={order.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Order #{order.id.slice(0, 8)}</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge className={getStatusColor(order.status)}>
                    {order.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                  {order.delivery_type === 'pickup' && (
                    <Badge variant="outline" className="text-xs">
                      PICKUP
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>Completed: {new Date(order.completed_at).toLocaleTimeString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  <span>{order.customer_name || 'Anonymous'}</span>
                </div>
                {order.customer_phone && (
                  <div className="flex items-center gap-1">
                    <Phone className="h-4 w-4" />
                    <span>{order.customer_phone}</span>
                  </div>
                )}
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Items:</h4>
                <div className="space-y-1">
                  {order.order_items?.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span>{item.quantity}x {item.menu_items?.name}</span>
                      <span>D{item.price.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {order.delivery_type === 'pickup' ? 'Pickup Order' : 'Delivery Address:'}
                  </p>
                  {order.delivery_type === 'delivery' && (
                    <p className="text-sm text-gray-600">{order.delivery_address}</p>
                  )}
                  {order.delivery_type === 'pickup' && (
                    <p className="text-sm text-gray-600">Customer picked up from restaurant</p>
                  )}
                </div>
              </div>

              {order.notes && (
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-sm font-medium mb-1">Customer Notes:</p>
                  <p className="text-sm text-gray-600">{order.notes}</p>
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t">
                <div className="text-lg font-semibold">
                  Total: D{order.total_amount.toFixed(2)}
                </div>
                <div className="text-sm text-gray-500">
                  Revenue: D{(order.total_amount - order.delivery_fee).toFixed(2)}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default CompletedOrdersView;
